import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponse } from '@angular/common/http';
import { Berater } from '../../shared/model/berater.model';
import { IFaehigkeit } from '../../shared/model/faehigkeit.model';
import { EducationComponent } from './education/education.component';
import { ProjectsComponent } from './projects/projects.component';
import { TrainingsComponent } from './trainings/trainings.component';
import { CareerComponent } from './career/career.component';
import { ActivatedRoute, Router } from '@angular/router';
import { BeraterService } from '../../services/berater.service';
import { INPUT_LIMITS } from '../../shared/constants/input-limits';
import { LinebreakPipe } from '../../shared/linebreak.pipe';
import { FormsModule } from '@angular/forms';
import { RolesBaComponent } from './ba-catalogues/roles-ba/roles-ba.component';
import { SkillsBaComponent } from './ba-catalogues/skills-ba/skills-ba.component';
import { UserRoleService } from '../../services/userRole.service';
import { AuthService } from '../../services/auth.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatError,
  MatFormField,
  MatInput,
  MatLabel,
} from '@angular/material/input';
import { MatDivider } from '@angular/material/divider';
import { SkillsComponent } from './skills/skills.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CertificatesComponent } from './certificates/certificates.component';
import { PersonalDataComponent } from './personal-data/personal-data.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { Katalog } from '../../shared/model/katalog.model';
import {
  CatalogAssignment,
  resolveAssignments,
} from '../../shared/model/catalog-assignment.model';
import { KatalogeService } from '../../services/kataloge.service';
import { MatIcon } from '@angular/material/icon';
import { ModalService } from '../../shared/components/modal/modal.service';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { AdminService } from '../../services/admin.service';
import { MessageService } from '../../shared/components/message.service';
import { MatBadge } from '@angular/material/badge';
import { MatCheckbox } from '@angular/material/checkbox';
import { Photos } from '../../shared/model/personaldaten.model';
import { normalizeToLanguageEntries } from '../../shared/constants/language-levels';
import { NgOptimizedImage } from '@angular/common';
import { finalize } from 'rxjs';
import { ExportService } from '../../services/export.service';
import { ImageService } from '../../services/image.service';
import {
  BreadcrumbSegment,
  BreadcrumbService,
} from '../../services/breadcrumb.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { FieldHintComponent } from '../../shared/components/field-hint/field-hint.component';
import {
  ExportConfiguration,
  ExportAction,
  ExportConfigurationInput,
  ExportLanguage,
  ExportPayload,
  ProjectsExportData,
  RenderPayload,
  SkillRating,
} from '../../shared/model/export-payload.model';
import {
  hasUnratedSkills,
  normalizeExportLanguage,
  resolveDefaultExportLanguage,
  toSkillRating,
} from '../../shared/model/export-skill-rating';
import { extractNameConflictConfigId } from '../../shared/errors/export-config-name-conflict';
import {
  MAX_TWO_PAGER_INDUSTRIES,
  SKILL_MATRIX_ROWS,
  skillCategoryCount,
  usedSkillRows,
} from '../../shared/constants/two-pager-limits';
import { TwopagerSaveDialogComponent } from './twopager/twopager-save-dialog.component';
import { IProjekt, isDraft } from '../../shared/model/projekt.model';
import { RequestsService } from '../../services/requests.service';
import {
  CompletenessSection,
  HIRE_DATE_ROLES,
  computeProfileCompleteness,
  fromBerater,
} from '../../shared/model/profile-completeness';
import { RequestType } from '../../shared/model/request.model';
import { ValidationFinding } from '../../shared/model/validation-finding';
import { FieldWarningComponent } from '../../shared/components/field-warning/field-warning.component';
import { ProfileSearchComponent } from './profile-search/profile-search.component';
import { ProfileSearchService } from './profile-search/profile-search.service';
import { ExportBarComponent } from './export-bar/export-bar.component';
import { ExportTemplateDialogComponent } from './export-template-dialog/export-template-dialog.component';
import { ExportSaveDialogComponent } from './export-save-dialog/export-save-dialog.component';
import {
  availableExportTemplates,
  defaultExportTemplates,
  ExportTemplate,
  ExportTemplateSelection,
  normalizeExportTemplates,
  sameExportTemplates,
} from '../../shared/model/export-templates';
import { ToastComponent } from '../../shared/components/toast/toast.component';

/**
 * Leere Auswahl je Abschnitt. `skills` ist bewusst schon belegt: das Template bindet die
 * Skill-Auswahl an zwei Kacheln, und ein dort per `?? []` erzeugtes Array haette in jedem
 * Change-Detection-Lauf eine neue Referenz — das Auswahl-Memo in `SkillsComponent` wuerde
 * damit jedes Mal verworfen und die Zeilenrechnung liefe wieder ueber alle Faehigkeiten.
 */
function emptyExportSelections(): Record<string, string[]> {
  return { skills: [] };
}

interface ExportErrorBody {
  title?: string;
  message?: string;
  parameters?: { violations?: Record<string, string> };
}

function fileExtensionFor(mime: string | undefined): string {
  if (mime?.includes('presentationml')) return 'pptx';
  if (mime?.includes('pdf')) return 'pdf';
  if (mime?.includes('zip')) return 'zip';
  if (mime?.startsWith('text/plain')) return 'txt';
  return 'docx';
}

function exportToastKey(twoPager: boolean, toTeams: boolean): string {
  if (twoPager) {
    return toTeams
      ? 'consultant.export.toast.twoPagerSent'
      : 'consultant.export.toast.twoPagerDownloaded';
  }
  return toTeams
    ? 'consultant.export.toast.profileSent'
    : 'consultant.export.toast.profileDownloaded';
}

/** Projekt-, Aufgaben- und Methodik-Ids der Projektkachel als eine flache, kollisionsfreie Menge. */
function projectsExportIds(data: ProjectsExportData): string[] {
  const nested = (
    selections: Record<string, string[]> | undefined,
    kind: string,
  ) =>
    Object.entries(selections ?? {}).flatMap(([uuid, ids]) =>
      (ids ?? []).map((id) => `${kind}:${uuid}:${id}`),
    );
  return [
    ...(data.selectedProjectUuids ?? []),
    ...nested(data.taskSelections, 'task'),
    ...nested(data.methodSelections, 'method'),
  ];
}

/** NP20-824: Abschnitts-Anker → Name des Abschnitts in der Trefferansage. */
const SEARCH_SECTION_LABEL_KEYS: Record<string, string> = {
  header: 'consultant.subheader.profile',
  personal: 'consultant.subheader.staff',
  education: 'consultant.subheader.education',
  career: 'consultant.subheader.career',
  trainings: 'consultant.subheader.trainingCourses',
  certificates: 'consultant.subheader.certificates',
  projects: 'consultant.subheader.projects',
  skills: 'consultant.subheader.skills',
  'ba-profile': 'consultant.subheader.baProfile',
  'ba-skills': 'consultant.subheader.baSkills',
};

@Component({
  selector: 'app-consultant',
  imports: [
    EducationComponent,
    PersonalDataComponent,
    ProjectsComponent,
    TrainingsComponent,
    CareerComponent,
    LinebreakPipe,
    FormsModule,
    RolesBaComponent,
    SkillsBaComponent,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatDivider,
    SkillsComponent,
    TranslateModule,
    CertificatesComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatExpansionPanelHeader,
    MatIcon,
    ModalComponent,
    MatError,
    MatBadge,
    MatIconButton,
    NgOptimizedImage,
    MatCheckbox,
    MatProgressSpinner,
    MatTooltip,
    FieldHintComponent,
    TwopagerSaveDialogComponent,
    FieldWarningComponent,
    ProfileSearchComponent,
    ExportBarComponent,
    ExportTemplateDialogComponent,
    ExportSaveDialogComponent,
    ToastComponent,
  ],
  providers: [ProfileSearchService],
  templateUrl: './consultant.component.html',
  styleUrl: './consultant.component.scss',
  standalone: true,
})
export class ConsultantComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SkillsComponent) skillComponent!: SkillsComponent;
  protected get LIMITS() {
    return INPUT_LIMITS;
  }

  // NP20-828: Die Section-Badges wurden urspruenglich als Getter implementiert.
  // Weil ConsultantComponent Default-CD nutzt und einen Scroll-Listener in der
  // Angular-Zone haelt, wurde jedes Getter-Feld ~8x pro CD-Zyklus ausgewertet und
  // pro Task ein neues Aufgabe-Objekt erzeugt (Reviewer-Hinweis PR #617).
  // Jetzt sind es einfache Felder, die nur bei tatsaechlichen Datenaenderungen
  // via recomputeCounts() neu berechnet werden.
  missingProfilCount = 0;
  missingCertificatesCount = 0;
  missingSkillCount = 0;
  missingEducationCount = 0;
  missingCareerCount = 0;
  missingTrainingCount = 0;
  missingPersonalDataCount = 0;
  projectValidationErrorCount = 0;

  // NP20-1093: Dieselbe Pruefung liefert jetzt die Fundstellen; die Zaehler darueber
  // sind daraus abgeleitet, damit Badge-Zahl und Hinweistext nicht auseinanderlaufen.
  certificateFindings: ValidationFinding[] = [];
  skillFindings: ValidationFinding[] = [];
  educationFindings: ValidationFinding[] = [];
  careerFindings: ValidationFinding[] = [];
  trainingFindings: ValidationFinding[] = [];
  personalDataFindings: ValidationFinding[] = [];
  projectFindings: ValidationFinding[] = [];
  private introFindingSource: ValidationFinding | null = null;
  private photoFindingSource: ValidationFinding | null = null;

  get introFinding(): ValidationFinding | null {
    return this.exportMode ? null : this.introFindingSource;
  }

  get photoFinding(): ValidationFinding | null {
    return this.exportMode ? null : this.photoFindingSource;
  }

  /**
   * NP20-909: Bewusst ein Getter, anders als die Zaehler darueber. Er liest assignedCatalogs,
   * das erst nach dem Aufloesen der Master-Kataloge steht — ein einmalig im Setter berechnetes
   * Feld waere zu diesem Zeitpunkt noch leer und der Banner bliebe bis zum Neuladen stehen.
   *
   * Der Performance-Grund aus NP20-828 greift hier nicht: dieser Getter liest sieben Felder und
   * ruft getTotalMissingBaSkills(), das die Vorlage ohnehin zweimal pro Zyklus auswertet. Die
   * teuren Aufrufe (listProjectFindings mit neuen Aufgabe-Objekten) bleiben in den Feldern.
   */
  /** NP20-1057: Entwürfe sieht nur der Besitzer; Dritte bekommen vom Backend ohnehin nur freigegebene Stände. */
  isOwnProfile = false;

  /** In recomputeCounts gepflegt wie die übrigen Zähler — das Template liest ihn mehrmals pro Zyklus. */
  draftProjectCount = 0;

  /** Mail-Link der Entwurfs-Erinnerung: `?section=projects&project=<uuid>`. */
  deepLinkedProjectUuid: string | null = null;

  private openDeepLinkedProject(isOwnProfile: boolean): void {
    const query = this.route.snapshot.queryParamMap;
    if (!isOwnProfile || query?.get('section') !== 'projects') return;
    this.deepLinkedProjectUuid = query.get('project');
    // erst nach dem Rendern der Projektliste, sonst fehlt der Anker noch
    setTimeout(() => this.scrollTo('projects'));
  }

  jumpToDraft(): void {
    // Erst zurücksetzen: ein zweiter Klick auf denselben Entwurf soll ihn wieder öffnen.
    this.deepLinkedProjectUuid = null;
    const uuid = this.selectedBerater?.projekte?.find(projekt => isDraft(projekt))?.uuid ?? null;
    setTimeout(() => (this.deepLinkedProjectUuid = uuid));
    this.scrollTo('projects');
  }

  get hasValidationIssues(): boolean {
    return (
      this.missingProfilCount > 0 ||
      this.missingPersonalDataCount > 0 ||
      this.missingEducationCount > 0 ||
      this.missingCareerCount > 0 ||
      this.missingTrainingCount > 0 ||
      this.missingCertificatesCount > 0 ||
      this.projectValidationErrorCount > 0 ||
      this.missingSkillCount > 0 ||
      this.getTotalMissingBaSkills() > 0
    );
  }

  private _selectedBerater: Berater;

  get selectedBerater(): Berater {
    return this._selectedBerater;
  }

  set selectedBerater(value: Berater) {
    this._selectedBerater = value;
    this.recomputeCounts();
    this.refreshAssignedCatalogs();
  }

  /**
   * NP20-889: Die anzeigbaren BA-Kataloge. Der Berater traegt seit dem Wegfall der eingebetteten
   * Kopien nur noch catalogId und Bewertungen — Katalogname, Preisgruppe, RVSE-Version und die
   * vollstaendige Kenntnisliste kommen live aus dem Master-Katalog.
   */
  assignedCatalogs: Katalog[] = [];

  /**
   * Einmal geladene Master bleiben fuer die Dauer des Seitenaufrufs stehen. Ein Speichern aendert
   * nur die Bewertungen, nicht den Katalogtext — ohne den Cache wuerde jede Bewertung erneut
   * saemtliche Kataloge nachladen.
   */
  private readonly catalogMasterById = new Map<string, Katalog>();

  private refreshAssignedCatalogs(): void {
    const assignments = this._selectedBerater?.catalogAssignments ?? [];
    const missing = assignments
      .map((a) => a.catalogId)
      .filter((id) => !this.catalogMasterById.has(id));

    if (missing.length === 0) {
      this.applyAssignedCatalogs(assignments);
      return;
    }

    // Teilergebnisse sind moeglich und gewollt: `getByIds` faengt den Fehler je Katalog ab
    // (Review PR #702). Nur die Zuweisung, deren Master nicht geladen werden konnte, fehlt
    // dann in der Anzeige — die uebrigen stehen weiter da. Sie wird beim naechsten Aufruf
    // erneut versucht, weil hier nur Erfolge in den Cache wandern.
    //
    // Kein error-Zweig mehr: der hat die Lage frueher verschlimmert. Er rief
    // `applyAssignedCatalogs` mit unbefuelltem Cache auf, und `resolveAssignments` verwirft
    // jede Zuweisung ohne Master — ein einziger fehlschlagender GET leerte damit den ganzen
    // BA-Abschnitt. Bewusst weiterhin ohne Fehlermeldung: die Profilseite soll deswegen nicht
    // unterbrochen werden.
    this.katalogService.getByIds(missing).subscribe((masters) => {
      masters.forEach((master) =>
        this.catalogMasterById.set(master.id, master),
      );
      this.applyAssignedCatalogs(assignments);
    });
  }

  private applyAssignedCatalogs(assignments: CatalogAssignment[]): void {
    this.assignedCatalogs = resolveAssignments(
      assignments,
      this.catalogMasterById,
    );
    this.initPanelState();
  }

  /**
   * NP20-889: Nach dem Speichern aus RolesBa oder SkillsBa liefert das Backend die vollstaendige
   * Zuweisungsliste. Der Parent uebernimmt sie und loest sie neu auf — nur fuer neu hinzugekommene
   * Kataloge faellt dabei ein Nachladen an.
   */
  onCatalogAssignmentsSaved(assignments: CatalogAssignment[]) {
    this.selectedBerater = {
      ...this.selectedBerater,
      catalogAssignments: assignments,
    };
  }

  /**
   * NP20-828: Aktualisiert alle Section-Badge-Zaehler auf Basis des aktuellen
   * _selectedBerater. Wird sowohl im setter als auch nach Kind-Emissions
   * (onProjectSaved, onSectionSaved, onProjectFaehigkeitenReloaded) aufgerufen —
   * die Kinder liefern immer neue Objekt-Referenzen, sodass eine In-Place-Mutation
   * (die den alten Getter-Ansatz motivierte) hier kein Thema mehr ist.
   */
  private recomputeCounts(): void {
    const completeness = computeProfileCompleteness(
      fromBerater(this._selectedBerater),
      {
        knownIndustryNames: this.knownIndustryNames,
        knownMainFocusNames: this.knownMainFocusNames,
        hireDateRequired: this.hireDateRequiredFor(this._selectedBerater),
      },
    );
    const section = (name: CompletenessSection) => completeness.sections[name];
    this.missingProfilCount = section('profil').badges;
    this.certificateFindings = section('certificates').findings;
    this.missingCertificatesCount = section('certificates').badges;
    this.skillFindings = section('skills').findings;
    this.missingSkillCount = section('skills').badges;
    this.educationFindings = section('education').findings;
    this.missingEducationCount = section('education').badges;
    this.careerFindings = section('career').findings;
    this.missingCareerCount = section('career').badges;
    this.trainingFindings = section('trainings').findings;
    this.missingTrainingCount = section('trainings').badges;
    this.personalDataFindings = section('personal').findings;
    this.missingPersonalDataCount = section('personal').badges;
    this.projectFindings = section('projects').findings;
    this.projectValidationErrorCount = section('projects').badges;
    this.introFindingSource = this.findIntroFinding(this._selectedBerater);
    this.photoFindingSource = this.findPhotoFinding(this._selectedBerater);
    this.draftProjectCount = this.isOwnProfile
      ? (this._selectedBerater?.projekte ?? []).filter(projekt => isDraft(projekt)).length
      : 0;
  }

  private findIntroFinding(
    value: Berater | null | undefined,
  ): ValidationFinding | null {
    const intro = value?.personaldaten?.intro ?? '';
    if (intro.length === 0) {
      return {
        section: 'personal',
        field: 'intro',
        labelKey: 'consultant.introLabel',
        reasonKey: 'consultant.introRequired',
      };
    }
    if (intro.length < INPUT_LIMITS.INTRO_MIN) {
      return {
        section: 'personal',
        field: 'intro',
        labelKey: 'consultant.introLabel',
        reasonKey: 'general.validation.minLength',
        reasonParams: { min: INPUT_LIMITS.INTRO_MIN },
      };
    }
    return null;
  }

  private findPhotoFinding(
    value: Berater | null | undefined,
  ): ValidationFinding | null {
    const photos = value?.personaldaten?.photos ?? [];
    if (photos.length === 0) {
      return {
        section: 'profil',
        field: 'photo',
        labelKey: 'consultant.photoRequired',
        reasonKey: 'consultant.photoRequired',
      };
    }
    return null;
  }

  private hireDateRequiredFor(value: Berater | null | undefined): boolean {
    const canSeeEntryDate = this.userService.includesRoles(HIRE_DATE_ROLES);
    return canSeeEntryDate && !value?.applicant;
  }

  activeSection = 'header';
  private readonly sectionIds = [
    'header',
    'personal',
    'education',
    'career',
    'trainings',
    'certificates',
    'projects',
    'skills',
    'ba-profile',
    'ba-skills',
  ];
  private readonly stickyHeadersHeightPx = 160;
  private rafId: number | null = null;
  private scrollLocked = false;
  private scrollLockTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly onScroll = (): void => {
    if (this.scrollLocked || this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (this.scrollLocked) return;
      const atBottom =
        window.innerHeight + window.scrollY >= document.body.scrollHeight - 50;
      const offset = atBottom ? window.innerHeight : this.stickyHeadersHeightPx;
      let current = this.sectionIds[0];
      for (const id of this.sectionIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = id;
        }
      }
      this.activeSection = current;
    });
  };

  editMode: boolean = false;
  summaryCopy: string;
  // NP20-889: Nach Katalog-Id statt nach Index, damit ein Speichern den auf-/zugeklappten Zustand
  // nicht verwirft, wenn sich die Liste der zugewiesenen Kataloge aendert.
  panelOpenState: Record<string, boolean> = {};
  originalConsultant: Berater;
  editConsultantModalShow: boolean = false;
  seePicturesModalShow: boolean = false;
  showDeleteModal: boolean = false;
  selectedImages: Photos[] = [];
  selectedIndex: number;
  defaultPhoto = './assets/img/no-picture.png';
  thumbnailUrls: Map<string, string> = new Map();
  replacingIndex: number | null = null;
  isExporting = false;
  /** Laeuft das Speichern vor einem Lauf, gilt der Lauf schon als gestartet (B11). */
  private savingConfig = false;
  /** Ziel des naechsten Laufs; bleibt ueber Speichern-Modal und Namenskonflikt erhalten. */
  private pendingExportAction: ExportAction = 'download';
  toastMessage: string | null = null;
  showTemplateDialog = false;
  /** Im Konstruktor belegt: Feld-Initializer mit importierten Funktionen sind im Vitest-Gesamtlauf unzuverlaessig. */
  exportTemplates: ExportTemplateSelection[];
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly profileSearchService = inject(ProfileSearchService);
  exportMode: boolean = false;
  twoPagerMode: boolean = false;
  twoPagerPageCount: number = 2;
  // NP20-958: Sprache der statischen Beschriftungen im erzeugten Dokument.
  exportLanguage: ExportLanguage = 'de';
  readonly exportLanguageOptions: readonly ExportLanguage[] = ['de', 'en'];
  includeProfilePicture: boolean = true;
  exportSelections: Record<string, string[]> = emptyExportSelections();
  profileName: string = '';
  showExportNameModal: boolean = false;
  exportConfigurations: ExportConfiguration[] = [];
  selectedConfigId: string | null = null;
  showDeleteConfigModal: boolean = false;
  showCloseExportModal: boolean = false;
  closeExportProfileName: string = '';
  configDirty: boolean = false;
  showPreselectionHint: boolean = false;
  /** Je Abschnitt die Ids, die er seit dem Oeffnen des Exports schon gemeldet hat. */
  private readonly preselectionBaseline = new Map<string, Set<string>>();
  /** Je Abschnitt die zuletzt gemeldeten Ids, noch nicht mit der Baseline verrechnet. */
  private readonly preselectionReports = new Map<string, Set<string>>();
  /** Zaehlt die Oeffnungen, damit eine verzoegerte Abmeldung nicht in die naechste faellt. */
  private preselectionRun: number = 0;
  private preselectionSettlePending: boolean = false;
  private applyingConfig: boolean = false;
  private viewReady: boolean = false;
  forcedEducationIds: string[] | null = null;
  forcedCareerIds: string[] | null = null;
  forcedTrainingIds: string[] | null = null;
  forcedCertificateIds: string[] | null = null;
  forcedSchwerpunkte: string[] | null = null;
  forcedBranchen: string[] | null = null;
  forcedSkillIds: string[] | null = null;
  forcedProjectsData: ProjectsExportData | null = null;
  showTemplateConflictModal: boolean = false;
  /** Rueckfragen bei einer geladenen, geaenderten Vorlage — beim Export bzw. beim Schliessen. */
  showLoadedTemplateModal: boolean = false;
  showCloseTemplateModal: boolean = false;
  conflictingTemplateName: string = '';
  /** configId der Vorlage, die den Namen schon belegt — vom Backend im 409 genannt. */
  private conflictingConfigId: string | null = null;
  private pendingSave: { name: string; followUp: 'render' | 'close' } | null =
    null;
  projectsExportData: ProjectsExportData = {
    selectedProjectUuids: [],
    taskSelections: {},
    methodSelections: {},
  };

  get totalExportCount(): number {
    return Object.values(this.exportSelections).reduce(
      (sum, ids) => sum + ids.length,
      0,
    );
  }

  get twoPagerTotalSkillCount(): number {
    return this.exportSelections['skills']?.length ?? 0;
  }

  // Distinkte Skill-Kategorien der aktuellen Auswahl (jede belegt im Generator eine eigene Zeile).
  get twoPagerSkillCategoryCount(): number {
    return skillCategoryCount(
      this.selectedBerater?.faehigkeiten,
      this.exportSelections['skills'] ?? [],
    );
  }

  /**
   * Die Checkliste zaehlt die Zeilen der Skill-Matrix, nicht die Fähigkeiten (`SKILL_SLOTS` im
   * Generator). Vorher stand dort ein Fähigkeiten-Maximum, das mit jeder Kategorie sank — die
   * Obergrenze wirkte dadurch beweglich, obwohl sie fest ist. Gezaehlt wird jetzt der Verbrauch:
   * jede Fähigkeit und jede Kategorie-Ueberschrift belegt eine der 18 Zeilen.
   */
  get twoPagerSkillRowsUsed(): number {
    return usedSkillRows(
      this.selectedBerater?.faehigkeiten,
      this.exportSelections['skills'] ?? [],
    );
  }

  get twoPagerSkillRowsMax(): number {
    return SKILL_MATRIX_ROWS;
  }

  get twoPagerSkillBudgetFull(): boolean {
    return this.twoPagerSkillRowsUsed >= SKILL_MATRIX_ROWS;
  }

  get twoPagerSchwerpunkteCount(): number {
    return this.exportSelections['personal']?.length ?? 0;
  }

  get twoPagerBranchenCount(): number {
    return this.exportSelections['industries']?.length ?? 0;
  }

  get twoPagerMaxBranchen(): number {
    return MAX_TWO_PAGER_INDUSTRIES;
  }

  // NP20-958: Der Generator rendert die Skill-Matrix aus dem Profil-Level. Eine ausgewaehlte,
  // aber unbewertete Faehigkeit wuerde dort leer stehen — deshalb blockiert sie das Speichern.
  get hasUnratedSelectedSkills(): boolean {
    return hasUnratedSkills(
      this.selectedBerater?.faehigkeiten,
      this.exportSelections['skills'] ?? [],
    );
  }

  // Namenskonflikte gelten je (Berater, Name, Typ). Die Liste dient nur dem Dialog, damit ein
  // neuer Name nicht sichtbar erneut kollidiert — entscheidend ist der 409 des Backends.
  get existingTemplateNames(): string[] {
    return this.filteredExportConfigurations.map((c) => c.name).filter(Boolean);
  }

  get twoPagerCertCount(): number {
    return this.exportSelections['certificates']?.length ?? 0;
  }

  get twoPagerProjectCount(): number {
    return this.projectsExportData.selectedProjectUuids.length;
  }

  // Max. auswählbare Projekte je Seitenanzahl: 1 Seite → nur Profil, sonst 3 pro Projektseite.
  get maxProjectsForPages(): number {
    if (this.twoPagerPageCount <= 1) return 0;
    return this.twoPagerPageCount === 2 ? 3 : 6;
  }

  // Min. sinnvolle Projektanzahl je Seitenanzahl (1 Seite → 0, 2 Seiten → 1, 3 Seiten → 2).
  get minProjectsForPages(): number {
    if (this.twoPagerPageCount <= 1) return 0;
    return this.twoPagerPageCount === 2 ? 1 : 2;
  }

  get twoPagerProjectsValid(): boolean {
    return (
      this.twoPagerProjectCount >= this.minProjectsForPages &&
      this.twoPagerProjectCount <= this.maxProjectsForPages
    );
  }

  // Pro ausgewähltem Projekt: Aufgaben-, Methodik- und (aus den Tasks stammende) Skill-Anzahl für die Checkliste.
  get twoPagerProjectBreakdown(): {
    uuid: string;
    name: string;
    taskCount: number;
    methodCount: number;
    skillCount: number;
  }[] {
    const data = this.projectsExportData;
    const selectedSkills = new Set(this.exportSelections['skills'] ?? []);
    return (data.selectedProjectUuids ?? []).map((uuid) => {
      const project = this.selectedBerater?.projekte?.find(
        (p) => p.uuid === uuid,
      );
      const taskSkillIds = new Set(
        (project?.tasks ?? []).flatMap((t) =>
          (t.skills ?? []).map((s) => s.id),
        ),
      );
      return {
        uuid,
        name: project?.projektname ?? '',
        taskCount: (data.taskSelections[uuid] ?? []).length,
        methodCount: (data.methodSelections[uuid] ?? []).length,
        skillCount: [...selectedSkills].filter((id) => taskSkillIds.has(id))
          .length,
      };
    });
  }

  get filteredExportConfigurations() {
    const targetType = this.twoPagerMode ? 'TWO_PAGER' : 'STANDARD';
    return this.exportConfigurations.filter((c) => c.type === targetType);
  }

  isSectionAllSelected(section: string, total: number): boolean {
    if (total === 0) return false;
    return (this.exportSelections[section]?.length ?? 0) >= total;
  }

  isSectionSomeSelected(section: string): boolean {
    return (this.exportSelections[section]?.length ?? 0) > 0;
  }

  toggleSectionAll(
    section: 'education' | 'career' | 'trainings' | 'certificates',
    checked: boolean,
  ): void {
    const ids = checked ? this.getAllSectionIds(section) : [];
    switch (section) {
      case 'education':
        this.forcedEducationIds = ids;
        setTimeout(() => (this.forcedEducationIds = null));
        break;
      case 'career':
        this.forcedCareerIds = ids;
        setTimeout(() => (this.forcedCareerIds = null));
        break;
      case 'trainings':
        this.forcedTrainingIds = ids;
        setTimeout(() => (this.forcedTrainingIds = null));
        break;
      case 'certificates':
        this.forcedCertificateIds = ids;
        setTimeout(() => (this.forcedCertificateIds = null));
        break;
    }
  }

  private getAllSectionIds(section: string): string[] {
    const b = this.selectedBerater;
    switch (section) {
      case 'education':
        return (b.ausbildung ?? [])
          .map((a) => a.uuid)
          .filter((u): u is string => !!u);
      case 'career':
        return (b.werdegang ?? [])
          .map((w) => w.uuid)
          .filter((u): u is string => !!u);
      case 'trainings':
        return (b.schulungen ?? [])
          .map((s) => s.uuid)
          .filter((u): u is string => !!u);
      case 'certificates':
        return (b.zertifikate ?? [])
          .map((z) => z.uuid)
          .filter((u): u is string => !!u);
      default:
        return [];
    }
  }

  constructor(
    @Inject(AuthService) public auth: AuthService,
    protected userService: UserRoleService,
    private route: ActivatedRoute,
    private beraterService: BeraterService,
    private katalogService: KatalogeService,
    private router: Router,
    private modalService: ModalService,
    private adminService: AdminService,
    private messageService: MessageService,
    private exportService: ExportService,
    private imageService: ImageService,
    private breadcrumbService: BreadcrumbService,
    private requestsService: RequestsService,
    private translateService: TranslateService,
  ) {
    this.exportTemplates = defaultExportTemplates();
  }

  // NP20-828: Bekannte Branchen/Schwerpunkte fuer die Legacy-Erkennung im Badge-Zaehler.
  // Wird einmalig beim Init geladen; solange die Listen leer sind, zaehlt der Legacy-Check nicht mit.
  private knownIndustryNames = new Set<string>();
  private knownMainFocusNames = new Set<string>();

  ngOnInit(): void {
    this.getUserFromRoute();
    // NP20-828: Katalog-Namen fuer Legacy-Erkennung. Der Service teilt den
    // shareReplay-Stream zwischen ConsultantComponent und PersonalDataComponent,
    // sodass beide Consumer denselben HTTP-Roundtrip nutzen.
    this.requestsService
      .getApprovedNames$(RequestType.INDUSTRY)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((set) => {
        this.knownIndustryNames = set;
        this.recomputeCounts();
      });
    this.requestsService
      .getApprovedNames$(RequestType.MAINFOCUS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((set) => {
        this.knownMainFocusNames = set;
        this.recomputeCounts();
      });
  }

  ngAfterViewInit(): void {
    this.profileSearchService.configure({
      root: this.host.nativeElement,
      sectionAnchorSelector: 'a.consultant__section-anchor[id]',
      sectionLabelKey: (id) => SEARCH_SECTION_LABEL_KEYS[id] ?? null,
    });
    setTimeout(() => {
      this.viewReady = true;
    }, 0);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  ngOnDestroy(): void {
    this.thumbnailUrls.forEach((url) => URL.revokeObjectURL(url));
    document.body.classList.remove('export-mode-active');
    window.removeEventListener('scroll', this.onScroll);
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.scrollLockTimeout !== null) clearTimeout(this.scrollLockTimeout);
  }

  onImageError(event: any) {
    event.target.src = this.defaultPhoto;
  }

  getTotalMissingBaSkills(): number {
    return this.assignedCatalogs
      .map((catalog) => this.getMissingBaSkillCount(catalog))
      .reduce((sum, current) => sum + current, 0);
  }

  onReplacePhoto(event: any, index: number) {
    const file: File = event.target.files[0];
    if (!file || !this.selectedBerater) return;

    const photoId = this.selectedImages[index]?.photoId;
    if (!photoId) return;

    this.replacingIndex = index;

    this.imageService
      .replacePhoto(this.selectedBerater.id, photoId, file)
      .subscribe({
        next: () => {
          this.reloadPhotos(() => {
            this.replacingIndex = null;
          });
          this.messageService.success('consultant.imageModal.photoReplaced');
        },
        error: () => {
          this.replacingIndex = null;
          this.messageService.error('consultant.imageModal.error');
        },
      });

    event.target.value = null;
  }

  private reloadPhotos(onDone?: () => void): void {
    const userLogin = this.route.snapshot.paramMap.get('id');
    const consultantId =
      userLogin === 'me' || !userLogin
        ? this.userService.getConsultantID()
        : userLogin;

    this.beraterService.find(consultantId).subscribe({
      next: (response: HttpResponse<Berater>) => {
        const berater = response.body;
        this.selectedBerater = berater;

        const newPhotos = berater.personaldaten.photos.slice(0, 3) ?? [];
        this.selectedImages = newPhotos;
        this.originalConsultant = JSON.parse(JSON.stringify(berater));

        if (!newPhotos.length) {
          onDone?.();
          return;
        }

        let loaded = 0;
        for (const photo of newPhotos) {
          this.imageService.getThumbnail(berater.id, photo.photoId).subscribe({
            next: (blob: Blob) => {
              this.thumbnailUrls = new Map(this.thumbnailUrls);
              this.thumbnailUrls.set(photo.photoId, URL.createObjectURL(blob));
              loaded++;
              if (loaded === newPhotos.length) onDone?.();
            },
            error: () => {
              loaded++;
              if (loaded === newPhotos.length) onDone?.();
            },
          });
        }
      },
      error: () => {
        this.replacingIndex = null;
        this.messageService.error('consultant.imageModal.error');
      },
    });
  }

  onFilesSelectedModal(event: any) {
    const files: FileList = event.target.files;
    if (!files || !this.selectedBerater) return;

    Array.from(files).forEach((file) => {
      const localUrl = URL.createObjectURL(file);

      this.imageService.uploadPhoto(this.selectedBerater.id, file).subscribe({
        next: (data: Berater) => {
          const lastPhoto = data.personaldaten.photos?.slice(-1)[0];
          if (!lastPhoto) return;

          if (!this.selectedImages) this.selectedImages = [];

          if (!this.selectedImages.length) {
            this.selectedImages = [lastPhoto];
          } else if (this.selectedImages.length < 3) {
            this.selectedImages.push(lastPhoto);
          }

          this.selectedBerater.personaldaten.photos = data.personaldaten.photos ?? [];
          this.recomputeCounts();

          this.thumbnailUrls.set(lastPhoto.photoId, localUrl);
          this.seePicturesModalShow = true;
        },
        error: () => {
          URL.revokeObjectURL(localUrl);
          this.messageService.error('consultant.imageModal.error');
        },
      });
    });

    event.target.value = null;
  }

  private resolveParentFromUrl(url: string | null): BreadcrumbSegment | null {
    if (!url) return null;
    if (url.startsWith('/berater-list'))
      return {
        label: 'nav.consultants',
        link: ['/berater-list'],
        translate: true,
      };
    if (url.startsWith('/bench'))
      return { label: 'nav.bench', link: ['/bench'], translate: true };
    return null;
  }

  getUserFromRoute(): void {
    const userLogin = this.route.snapshot.paramMap.get('id');
    const isOwnProfile = userLogin === 'me' || !userLogin;
    this.isOwnProfile = isOwnProfile;
    const consultantId = isOwnProfile
      ? this.userService.getConsultantID()
      : userLogin;

    this.beraterService.find(consultantId).subscribe({
      next: (response: HttpResponse<Berater>) => {
        const berater = response.body;

        this.selectedBerater = berater;
        this.openDeepLinkedProject(isOwnProfile);

        if (!isOwnProfile) {
          const parent = this.resolveParentFromUrl(
            this.breadcrumbService.getPreviousUrl(),
          );
          const trail: BreadcrumbSegment[] = [
            ...(parent ? [parent] : []),
            {
              label: 'nav.profileOf',
              translate: true,
              params: { name: `${berater.nachname} ${berater.vorname}` },
            },
          ];
          this.breadcrumbService.setTrail(trail);
        }
        const newPhotos = berater.personaldaten.photos.slice(0, 3) ?? [];
        const newIds = newPhotos.map((p) => p.photoId).join(',');
        const oldIds =
          this.selectedImages?.map((p) => p.photoId).join(',') ?? '';
        if (newIds !== oldIds) {
          this.selectedImages = newPhotos;
        }
        this.originalConsultant = JSON.parse(JSON.stringify(berater));
        this.summaryCopy = berater.personaldaten.intro;

        this.loadThumbnails();
      },
      error: () => {
        this.messageService.error('consultant.imageModal.error');
      },
    });
  }

  loadThumbnails(): void {
    for (const photo of this.selectedImages) {
      if (photo.photoId && !this.thumbnailUrls.has(photo.photoId)) {
        this.imageService
          .getThumbnail(this.selectedBerater.id, photo.photoId)
          .subscribe({
            next: (blob: Blob) =>
              this.thumbnailUrls.set(photo.photoId, URL.createObjectURL(blob)),
          });
      }
    }

    const activeIds = new Set(this.selectedImages.map((p) => p.photoId));
    this.thumbnailUrls.forEach((url, id) => {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        this.thumbnailUrls.delete(id);
      }
    });
  }

  initPanelState(): void {
    // Bereits geoeffnete oder zugeklappte Panels behalten ihren Zustand, neue starten offen.
    this.assignedCatalogs.forEach((catalog) => {
      if (this.panelOpenState[catalog.id] === undefined) {
        this.panelOpenState[catalog.id] = true;
      }
    });
  }

  toggleAll(expanded: boolean): void {
    this.assignedCatalogs.forEach((catalog) => {
      this.panelOpenState[catalog.id] = expanded;
    });
  }

  scrollTo(sectionId: string) {
    this.activeSection = sectionId;
    this.scrollLocked = true;
    if (this.scrollLockTimeout !== null) clearTimeout(this.scrollLockTimeout);
    this.scrollLockTimeout = setTimeout(() => {
      this.scrollLocked = false;
      this.scrollLockTimeout = null;
    }, 1000);

    const el = document.getElementById(sectionId);
    if (el) {
      const yOffset = -this.stickyHeadersHeightPx;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  handleClick() {
    document.getElementById('upload-file').click();
  }

  handleClickReplace(index: number) {
    const inputEl = document.getElementById(
      `upload-file-replace-${index}`,
    ) as HTMLInputElement;
    if (inputEl) inputEl.click();
  }

  handleClickModalMultiple() {
    document.getElementById('upload-file-modal-multiple').click();
  }

  protected save() {
    if (!this.editMode || !this.hasChangesIntro()) return;

    this.beraterService
      .updateSummary(
        this.selectedBerater.personaldaten.intro,
        this.selectedBerater.id,
      )
      .pipe(
        finalize(() => {
          this.getUserFromRoute();
          this.editMode = false;
        }),
      )
      .subscribe({
        error: () => this.messageService.error('consultant.imageModal.error'),
      });
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      this.originalConsultant = JSON.parse(
        JSON.stringify(this.selectedBerater),
      );
      this.editMode = true;
    }
  }

  onCancelEdit() {
    this.selectedBerater = JSON.parse(JSON.stringify(this.originalConsultant));
    this.editMode = false;
  }

  hasChangesIntro(): boolean {
    return (
      this.selectedBerater?.personaldaten?.intro !==
      this.originalConsultant?.personaldaten?.intro
    );
  }

  hasChangesName(): boolean {
    return (
      JSON.stringify(this.selectedBerater?.vorname) !==
        JSON.stringify(this.originalConsultant?.vorname) ||
      JSON.stringify(this.selectedBerater?.nachname) !==
        JSON.stringify(this.originalConsultant?.nachname)
    );
  }

  reloadConsultant() {
    this.beraterService.find(this.selectedBerater.id).subscribe({
      next: (value) => {
        if (value?.body) {
          this.selectedBerater = value.body;
        }
      },
      error: () => this.messageService.error('consultant.imageModal.error'),
    });
  }

  // NP20-828: Nach einem Save aus einer Section (PersonalData, Education, Career,
  // Trainings) uebernimmt der Parent den vom BE zurueckgelieferten Berater direkt,
  // statt via find() neu zu laden. Vorteile:
  // - Keine parallelen Formulare anderer Sektionen verlieren ihren editMode.
  // - Nur ein PUT-Roundtrip, kein zusaetzlicher GET.
  // - Die Section-Badges rechnen sofort nach (neue Referenz triggert die Getter).
  onSectionSaved(berater: Berater | null | undefined) {
    if (berater) {
      this.selectedBerater = berater;
    }
  }

  onProjectSaved(projects: IProjekt[]) {
    this.selectedBerater = { ...this.selectedBerater, projekte: projects };
  }

  onProjectFaehigkeitenReloaded(faehigkeiten: IFaehigkeit[]) {
    this.selectedBerater = { ...this.selectedBerater, faehigkeiten };
  }

  getMissingBaSkillCount(catalog: Katalog): number {
    return (catalog?.kenntnisse ?? []).filter(
      (item) => item.bewertung === 0 || item.bewertung === null,
    ).length;
  }

  hasMissingBaSkills(catalog: Katalog): boolean {
    return this.getMissingBaSkillCount(catalog) > 0;
  }

  editConsultantName() {
    this.editConsultantModalShow = true;
  }

  closeEditModal() {
    this.selectedBerater = JSON.parse(JSON.stringify(this.originalConsultant));
    this.modalService.close('editConsultant');
    this.editConsultantModalShow = false;
  }

  saveConsultant() {
    this.adminService.editConsultant(this.selectedBerater).subscribe({
      next: () => {
        this.originalConsultant = JSON.parse(
          JSON.stringify(this.selectedBerater),
        );
      },
      error: () => {
        this.messageService.error('consultant.editNameModal.error');
      },
    });
  }

  isValid() {
    if (this.selectedBerater.vorname === '') return false;
    if (this.selectedBerater.vorname.length >= 50) return false;
    if (this.selectedBerater.nachname === '') return false;
    if (this.selectedBerater.nachname.length >= 50) return false;
    return true;
  }

  saveApplicantChange() {
    this.beraterService.updateApplicant(this.selectedBerater).subscribe({
      next: () =>
        this.messageService.success('consultant.applicant.applicantSaved'),
      error: () =>
        this.messageService.error('consultant.applicant.applicantNotSaved'),
    });
  }

  saveFreelancerChange() {
    this.beraterService.updateFreelancer(this.selectedBerater).subscribe({
      next: () =>
        this.messageService.success('consultant.freelancer.freelancerSaved'),
      error: () =>
        this.messageService.error('consultant.freelancer.freelancerNotSaved'),
    });
  }

  setAsPrimary(index: number) {
    if (index === 0) return;

    const selected = this.selectedImages.splice(index, 1)[0];
    this.selectedImages.unshift(selected);

    const photoIds = this.selectedImages.map((img) => img.photoId);

    this.imageService
      .reorderPhotos(this.selectedBerater.id, photoIds)
      .subscribe({
        next: () => {
          this.getUserFromRoute();
          this.messageService.success('consultant.imageModal.primarySaved');
        },
        error: () => {
          this.messageService.error('consultant.imageModal.error');
          const original = [...this.selectedImages];
          this.selectedImages = original.sort((a, b) =>
            (a.photoId ?? '').localeCompare(b.photoId ?? ''),
          );
        },
      });
  }

  openModalPictures() {
    this.seePicturesModalShow = true;
  }

  deleteImage(index: number) {
    if (this.selectedImages.length <= 1) {
      this.messageService.error('consultant.imageModal.oneImage');
      return;
    }

    const image = this.selectedImages[index];
    const photoId = image?.photoId;
    const userId = this.selectedBerater?.id;

    if (!photoId || !userId) return;

    this.imageService.deletePhoto(userId, photoId).subscribe({
      next: () => {
        this.selectedImages.splice(index, 1);
        this.selectedBerater.personaldaten.photos = (this.selectedBerater.personaldaten.photos ?? []).filter(
          (p) => p.photoId !== photoId,
        );
        this.recomputeCounts();

        if (this.selectedImages.length === 0) {
          this.selectedIndex = null;
        } else if (index >= this.selectedImages.length) {
          this.selectedIndex = this.selectedImages.length - 1;
        }

        this.closeDeleteModal();
        this.messageService.success('consultant.imageModal.deleteImage');
      },
      error: () => {
        this.messageService.error('consultant.imageModal.error');
      },
    });
  }

  openDeleteModal(i: number) {
    this.selectedIndex = i;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.selectedIndex = null;
    this.showDeleteModal = false;
  }

  get canImportCv(): boolean {
    return this.userService.includesRoles(['Admin', 'Office', 'Sale']);
  }

  openAiProfileMatching(): void {
    if (this.selectedBerater?.id) {
      this.router.navigate(['aiProfileMatching', this.selectedBerater.id]);
    }
  }

  importCv() {
    if (this.canImportCv && this.selectedBerater?.id) {
      this.router.navigate(['cv-import', this.selectedBerater.id]);
    }
  }

  downloadImage(index: number) {
    const image = this.selectedImages[index];
    if (!image || !image.photoId || !this.selectedBerater?.id) {
      this.messageService.error('consultant.imageModal.downloadError');
      return;
    }

    const userId = this.selectedBerater.id;
    const photoId = image.photoId;

    this.imageService.getOriginalPhoto(userId, photoId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `berater_${this.selectedBerater.vorname}_${this.selectedBerater.nachname}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.error('consultant.imageModal.error');
      },
    });
  }

  getProfilePhoto(): string {
    const firstPhoto = this.selectedImages?.[0];
    if (!firstPhoto?.photoId) return this.defaultPhoto;
    return this.thumbnailUrls.get(firstPhoto.photoId) ?? this.defaultPhoto;
  }

  getThumbnailUrl(photo: Photos): string {
    if (!photo?.photoId) return this.defaultPhoto;
    return this.thumbnailUrls.get(photo.photoId) ?? this.defaultPhoto;
  }

  toggleExportMode() {
    if (this.exportMode) {
      this.closeExportProfileName = '';
      // Bei einer geladenen, geaenderten Vorlage steht dieselbe Frage an wie beim Export —
      // nur heisst die dritte Option hier "verwerfen" statt "ohne Speichern exportieren".
      if (
        this.configDirty &&
        this.selectedConfigId &&
        this.loadedTemplateName
      ) {
        this.showCloseTemplateModal = true;
        return;
      }
      this.showCloseExportModal = true;
    } else {
      this.exportMode = true;
      this.exportLanguage = this.defaultExportLanguage();
      this.loadExportConfigurations();
      this.beginPreselection();
      document.body.classList.add('export-mode-active');
    }
  }

  confirmCloseExport(): void {
    this.showCloseExportModal = false;
    this.exportMode = false;
    this.exportSelections = emptyExportSelections();
    this.profileName = '';
    this.exportConfigurations = [];
    this.selectedConfigId = null;
    this.configDirty = false;
    this.includeProfilePicture = true;
    this.exportTemplates = defaultExportTemplates();
    this.showTemplateDialog = false;
    this.endPreselection();
    this.twoPagerMode = false;
    this.twoPagerPageCount = 2;
    this.exportLanguage = this.defaultExportLanguage();
    this.showTemplateConflictModal = false;
    this.showLoadedTemplateModal = false;
    this.showCloseTemplateModal = false;
    this.conflictingTemplateName = '';
    this.conflictingConfigId = null;
    this.pendingSave = null;
    this.projectsExportData = {
      selectedProjectUuids: [],
      taskSelections: {},
      methodSelections: {},
    };
    document.body.classList.remove('export-mode-active');
  }

  saveConfigAndCloseExport(): void {
    if (this.exportBusy) return;
    const name = this.closeExportProfileName?.trim() ?? '';
    if (!this.validateBeforeSave(name)) return;
    this.showCloseExportModal = false;
    this.requestSave(name, 'close');
  }

  onCloseTemplateOverwrite(): void {
    if (this.exportBusy) return;
    const name = this.loadedTemplateName;
    const configId = this.selectedConfigId;
    if (!configId || !this.validateBeforeSave(name)) return;
    this.showCloseTemplateModal = false;
    this.pendingSave = { name, followUp: 'close' };
    this.persistConfig(name, 'close', configId);
  }

  onCloseTemplateSaveAs(newName: string): void {
    if (this.exportBusy) return;
    if (!this.validateBeforeSave(newName)) return;
    this.showCloseTemplateModal = false;
    this.requestSave(newName, 'close');
  }

  onCloseTemplateDiscard(): void {
    this.confirmCloseExport();
  }

  /** Vorbelegung der Dokumentsprache: die Sprache der Oberflaeche, sonst Deutsch. */
  private defaultExportLanguage(): ExportLanguage {
    return resolveDefaultExportLanguage(
      this.translateService.getCurrentLang() ??
        this.translateService.getDefaultLang(),
    );
  }

  /**
   * NP20-960 haelt die Einstellung an der Vorlage fest, also ist das Umschalten eine Aenderung
   * daran. Ohne das galt eine geladene Vorlage weiter als unveraendert und der Export lief ueber
   * ihre configId — mit dem alten Stand des Bildes.
   */
  toggleProfilePicture(): void {
    this.includeProfilePicture = !this.includeProfilePicture;
    // Das Abschalten ist eine Abwahl wie jede andere; die Kacheln melden sie nicht mit.
    if (!this.includeProfilePicture) this.endPreselection();
    // viewReady suppresses emissions during initial data load — those are not user changes.
    if (!this.applyingConfig && this.viewReady) {
      setTimeout(() => (this.configDirty = true), 0);
    }
  }

  setExportLanguage(language: ExportLanguage): void {
    if (this.exportLanguage === language) return;
    this.exportLanguage = language;
    // viewReady suppresses emissions during initial data load — those are not user changes.
    if (!this.applyingConfig && this.viewReady) {
      setTimeout(() => (this.configDirty = true), 0);
    }
  }

  /**
   * Die Bewertungspflicht haengt an jedem Export- und Speicherpfad, auch am Export ueber eine
   * unveraenderte Vorlage: die Bewertung einer enthaltenen Faehigkeit kann im Profil geleert
   * worden sein, seit die Vorlage gespeichert wurde.
   */
  private rejectsUnratedSkills(): boolean {
    if (this.twoPagerMode && this.hasUnratedSelectedSkills) {
      this.messageService.error('export.skill.rating.required');
      return true;
    }
    return false;
  }

  private validateBeforeSave(name: string): boolean {
    if (!name) {
      this.messageService.error('export.template.name.required');
      return false;
    }
    return !this.rejectsUnratedSkills();
  }

  private requestSave(name: string, followUp: 'render' | 'close'): void {
    this.pendingSave = { name, followUp };
    this.persistConfig(name, followUp);
  }

  onTemplateConflictUpdate(): void {
    if (this.exportBusy) return;
    const pending = this.pendingSave;
    if (!pending || !this.conflictingConfigId) return;
    this.showTemplateConflictModal = false;
    this.persistConfig(
      pending.name,
      pending.followUp,
      this.conflictingConfigId,
    );
  }

  onTemplateConflictSaveAs(newName: string): void {
    if (this.exportBusy) return;
    const pending = this.pendingSave;
    if (!pending) return;
    this.showTemplateConflictModal = false;
    this.requestSave(newName, pending.followUp);
  }

  onTemplateConflictCancelled(): void {
    this.pendingSave = null;
    this.conflictingTemplateName = '';
    this.conflictingConfigId = null;
  }

  /**
   * NP20-958: Ein belegter Vorlagenname ueberschreibt nicht mehr still — das Backend antwortet
   * auf den POST mit 409 und nennt die bestehende configId. Erst die Entscheidung des Nutzers
   * fuehrt auf den PUT dieser Vorlage (configId gesetzt) oder auf einen neuen Namen.
   */
  private persistConfig(
    name: string,
    followUp: 'render' | 'close',
    configId?: string,
  ): void {
    const payload = this.buildExportPayload(name);
    this.messageService.info('consultant.export.saving');
    const save$ = configId
      ? this.exportService.updateConfiguration(
          this.selectedBerater.id,
          configId,
          payload,
        )
      : this.exportService.submitProfile(this.selectedBerater.id, payload);
    this.savingConfig = true;
    save$.subscribe({
      next: (savedConfig) => {
        this.savingConfig = false;
        this.pendingSave = null;
        this.conflictingTemplateName = '';
        this.conflictingConfigId = null;
        this.showTemplateConflictModal = false;
        this.loadExportConfigurations();
        this.messageService.success('consultant.export.configSaved');
        if (followUp === 'close') {
          this.closeExportProfileName = '';
          this.confirmCloseExport();
          return;
        }
        this.selectedConfigId = savedConfig.id;
        this.configDirty = false;
        // Neben dem Namen der eben gespeicherten Vorlage waere der Hinweis irrefuehrend.
        this.endPreselection();
        this.renderExportPayload({
          configId: savedConfig.id,
          exactlyOneConfigSourceSet: true,
        });
      },
      error: (err) => {
        this.savingConfig = false;
        const existingConfigId = extractNameConflictConfigId(err);
        if (existingConfigId) {
          this.conflictingConfigId = existingConfigId;
          this.conflictingTemplateName = name;
          this.showTemplateConflictModal = true;
          return;
        }
        this.pendingSave = null;
        this.conflictingConfigId = null;
        this.messageService.error('consultant.export.error');
      },
    });
  }

  loadExportConfigurations(): void {
    if (!this.selectedBerater?.id) return;
    this.exportService.getConfigurations(this.selectedBerater.id).subscribe({
      next: (configs) => (this.exportConfigurations = configs.content ?? []),
      error: () => (this.exportConfigurations = []),
    });
  }

  onConfigSelected(configId: string): void {
    this.selectedConfigId = configId;
    const config = this.exportConfigurations.find((c) => c.id === configId);
    if (config) this.applyExportConfig(config);
  }

  applyExportConfig(config: ExportConfiguration): void {
    this.applyingConfig = true;
    this.endPreselection();
    this.twoPagerMode = config.type === 'TWO_PAGER';
    this.twoPagerPageCount = config.pageCount ?? 2;
    // Auch eine vor NP20-1061 gespeicherte STANDARD-Vorlage traegt eine Sprache ('DE') —
    // normalisiert wird deshalb unabhaengig vom Typ, Altbestand ohne Wert faellt auf die
    // Oberflaechensprache zurueck.
    this.exportLanguage =
      normalizeExportLanguage(config.language) ?? this.defaultExportLanguage();
    // NP20-960 haelt die Einstellung jetzt an der Vorlage fest; Altbestand ohne Wert bleibt beim Bild.
    this.includeProfilePicture = config.includeProfilePicture ?? true;
    // NP20-1285: Verfuegbar ist, was die Vorlage mit ihren eigenen Katalogen erzeugen kann.
    // Faellt dabei etwas weg (Katalog entzogen), gilt die Vorlage als geaendert.
    this.exportTemplates = normalizeExportTemplates(
      config.exportTemplates,
      availableExportTemplates(
        this.assignedCatalogs,
        (config.katalogSelections ?? []).map((k) => k.katalogId),
      ),
    );
    const templatesDropped =
      config.type === 'STANDARD' &&
      !sameExportTemplates(config.exportTemplates, this.exportTemplates);

    this.forcedEducationIds = [...(config.selectedAusbildungUuids ?? [])];
    this.forcedCareerIds = [...(config.selectedWerdegangUuids ?? [])];
    this.forcedTrainingIds = [...(config.selectedSchulungUuids ?? [])];
    this.forcedCertificateIds = [...(config.selectedZertifikatUuids ?? [])];
    this.forcedSchwerpunkte = [...(config.selectedSchwerpunkte ?? [])];
    this.forcedBranchen = [...(config.selectedBranchen ?? [])];

    const selectedProjectUuids = (config.projektSelections ?? []).map(
      (ps) => ps.projektUuid,
    );
    const taskSelections: Record<string, string[]> = {};
    const methodSelections: Record<string, string[]> = {};
    for (const ps of config.projektSelections ?? []) {
      taskSelections[ps.projektUuid] = (ps.aufgabenSelections ?? []).map(
        (a) => a.aufgabeUuid,
      );
      methodSelections[ps.projektUuid] = ps.selectedMethodikItems ?? [];
    }
    this.forcedProjectsData = {
      selectedProjectUuids,
      taskSelections,
      methodSelections,
    };

    this.exportSelections['education'] = this.forcedEducationIds;
    this.exportSelections['career'] = this.forcedCareerIds;
    this.exportSelections['trainings'] = this.forcedTrainingIds;
    this.exportSelections['certificates'] = this.forcedCertificateIds;
    this.exportSelections['personal'] = this.forcedSchwerpunkte;
    this.exportSelections['industries'] = this.forcedBranchen;
    // NP20-958: Die Faehigkeiten fehlten bisher komplett beim Laden — eine gespeicherte
    // Vorlage kam ohne Skill-Auswahl zurueck. Im Two-Pager ist app-skills controlled, dort
    // genuegt die Auswahl hier. Im STANDARD-Export haelt die Kachel ihre Haekchen selbst und
    // hat sich gerade alles vorgewaehlt — ohne `forcedSkillIds` zeigte sie alles an, waehrend
    // das Payload die Teilmenge traegt.
    this.forcedSkillIds = [...(config.selectedFaehigkeitIds ?? [])];
    this.exportSelections['skills'] = this.forcedSkillIds;

    setTimeout(() => {
      this.forcedEducationIds = null;
      this.forcedCareerIds = null;
      this.forcedTrainingIds = null;
      this.forcedCertificateIds = null;
      this.forcedSchwerpunkte = null;
      this.forcedBranchen = null;
      this.forcedSkillIds = null;
      this.forcedProjectsData = null;
      setTimeout(() => {
        this.applyingConfig = false;
        this.configDirty = templatesDropped;
      }, 0);
    }, 0);
  }

  openDeleteConfigModal(): void {
    this.showDeleteConfigModal = true;
  }

  deleteSelectedConfig(): void {
    if (!this.selectedConfigId || !this.selectedBerater?.id) return;
    this.showDeleteConfigModal = false;
    this.exportService
      .deleteConfiguration(this.selectedBerater.id, this.selectedConfigId)
      .subscribe({
        next: () => {
          this.exportConfigurations = this.exportConfigurations.filter(
            (c) => c.id !== this.selectedConfigId,
          );
          this.selectedConfigId = null;
          this.messageService.success('consultant.export.configDeleted');
        },
        error: () =>
          this.messageService.error('consultant.export.configDeleteError'),
      });
  }

  setTwoPagerMode(enabled: boolean): void {
    if (this.twoPagerMode !== enabled) {
      this.exportSelections = emptyExportSelections();
      this.twoPagerPageCount = 2;
      this.projectsExportData = {
        selectedProjectUuids: [],
        taskSelections: {},
        methodSelections: {},
      };
      if (enabled) {
        this.endPreselection();
      } else {
        this.beginPreselection();
      }
    }
    this.twoPagerMode = enabled;
    if (this.selectedConfigId) {
      const config = this.exportConfigurations.find(
        (c) => c.id === this.selectedConfigId,
      );
      if ((config?.type === 'TWO_PAGER') !== enabled) {
        this.selectedConfigId = null;
        this.configDirty = false;
        // Mit der Vorlage faellt auch ihre Sprache: der Export haengt jetzt an keiner
        // gespeicherten mehr. Sonst bliebe das 'DE' einer vor NP20-1061 gespeicherten
        // Vorlage still am neuen, unabhaengigen Export haengen.
        this.exportLanguage = this.defaultExportLanguage();
      }
    }
  }

  setPageCount(count: number): void {
    this.twoPagerPageCount = count;
    // viewReady suppresses emissions during initial data load — those are not user changes.
    // setTimeout prevents NG0100: child emissions during a CD cycle must not mutate parent state synchronously.
    if (!this.applyingConfig && this.viewReady) {
      setTimeout(() => (this.configDirty = true), 0);
    }
  }

  /** Ein Doppelklick darf keinen zweiten Lauf starten, auch nicht aus einem Modal (B11). */
  private get exportBusy(): boolean {
    return this.isExporting || this.savingConfig;
  }

  onSendClick(action: ExportAction = 'download'): void {
    if (this.exportBusy) return;
    this.pendingExportAction = action;
    // NP20-1285: Nur "Herunterladen" im Profil Export fragt nach Vorlagen; Teams legt alle ab.
    if (action === 'download' && !this.twoPagerMode) {
      this.showTemplateDialog = true;
      return;
    }
    this.continueExport();
  }

  /** Das Vorlagen-Modal steht vor dem Speichern-Ablauf; so ist die Auswahl schon im Payload. */
  onTemplatesConfirmed(selection: ExportTemplateSelection[]): void {
    if (this.exportBusy) return;
    this.showTemplateDialog = false;
    this.exportTemplates = selection;
    const loaded = this.exportConfigurations.find(
      (c) => c.id === this.selectedConfigId,
    );
    if (
      loaded &&
      !sameExportTemplates(
        selection,
        normalizeExportTemplates(loaded.exportTemplates, this.availableTemplates),
      )
    ) {
      this.configDirty = true;
    }
    this.continueExport();
  }

  onTemplatesCancelled(): void {
    this.showTemplateDialog = false;
  }

  get availableTemplates(): ExportTemplate[] {
    return availableExportTemplates(
      this.assignedCatalogs,
      this.exportSelections['rolesBa'] ?? [],
    );
  }

  /**
   * NP20-958: Eine geladene, aber geaenderte Vorlage darf beim Export nicht nach einem Namen
   * fragen, als waere sie neu — der Name steht ja schon fest. Stattdessen die Entscheidung,
   * die hier wirklich ansteht: bestehende Vorlage ueberschreiben, daneben speichern oder den
   * Export einmalig ohne Speichern erzeugen.
   */
  private continueExport(): void {
    if (this.selectedConfigId && !this.configDirty) {
      if (this.rejectsUnratedSkills()) return;
      this.renderExportPayload({
        configId: this.selectedConfigId,
        exactlyOneConfigSourceSet: true,
      });
      return;
    }
    if (this.selectedConfigId && this.loadedTemplateName) {
      this.showLoadedTemplateModal = true;
      return;
    }
    this.showExportNameModal = true;
  }

  get loadedTemplateName(): string {
    return (
      this.exportConfigurations.find((c) => c.id === this.selectedConfigId)
        ?.name ?? ''
    );
  }

  onLoadedTemplateOverwrite(): void {
    if (this.exportBusy) return;
    const name = this.loadedTemplateName;
    const configId = this.selectedConfigId;
    if (!configId || !this.validateBeforeSave(name)) return;
    this.showLoadedTemplateModal = false;
    this.pendingSave = { name, followUp: 'render' };
    this.persistConfig(name, 'render', configId);
  }

  onLoadedTemplateSaveAs(newName: string): void {
    if (this.exportBusy) return;
    if (!this.validateBeforeSave(newName)) return;
    this.showLoadedTemplateModal = false;
    this.requestSave(newName, 'render');
  }

  onLoadedTemplateSkipSave(): void {
    if (this.exportBusy) return;
    if (this.rejectsUnratedSkills()) return;
    this.showLoadedTemplateModal = false;
    this.exportWithoutSaving();
  }

  sendExportProfile(): void {
    if (this.exportBusy) return;
    const name = this.profileName?.trim() ?? '';
    if (!this.validateBeforeSave(name)) return;
    this.showExportNameModal = false;
    this.profileName = '';
    this.requestSave(name, 'render');
  }

  exportWithoutSaving(): void {
    if (this.exportBusy) return;
    if (this.rejectsUnratedSkills()) return;
    this.showExportNameModal = false;
    const vorname = this.selectedBerater?.vorname ?? '';
    const nachname = this.selectedBerater?.nachname ?? '';
    const isoDate = new Date().toISOString().slice(0, 10);
    const adHocName =
      [vorname, nachname, isoDate].filter(Boolean).join('-') || isoDate;
    const payload = this.buildExportPayload(adHocName);
    this.profileName = '';
    const inlineConfig: ExportConfigurationInput = {
      ...payload,
      id: '',
      beraterId: this.selectedBerater.id,
    };
    if (this.pendingExportAction === 'teams') delete inlineConfig.exportTemplates;
    this.renderExportPayload({ inlineConfig, exactlyOneConfigSourceSet: true });
  }

  private renderExportPayload(payload: RenderPayload): void {
    this.isExporting = true;
    // Die Sprache gehoert zum Auftrag, nicht zum Zeitpunkt der Antwort: der Umschalter
    // bleibt waehrend des Renderlaufs bedienbar, der Dateiname muss aber benennen, was
    // gerendert wurde.
    const language = this.exportLanguage;
    const toTeams = this.pendingExportAction === 'teams';
    const twoPager = this.twoPagerMode;
    this.messageService.info(
      toTeams ? 'consultant.export.teamsStarted' : 'consultant.export.started',
    );
    this.exportService
      .renderExport(this.selectedBerater.id, { ...payload, deliverToTeams: toTeams })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.isExporting = false;
          // Bei Teams ist der Body nur Bestaetigung; auch ein leerer gilt als Erfolg.
          if (!toTeams) {
            const blob = resp.body;
            if (!blob || blob.size === 0) {
              this.messageService.error('consultant.export.error');
              return;
            }
            const filename =
              this.parseContentDispositionFilename(
                resp.headers.get('Content-Disposition'),
              ) ?? this.fallbackExportFilename(blob.type, language);
            this.triggerBlobDownload(blob, filename);
          }
          this.confirmCloseExport();
          this.toastMessage = exportToastKey(twoPager, toTeams);
        },
        error: async (err) => {
          this.isExporting = false;
          const body = await this.readErrorBody(err);
          // Z6: gespeicherte Vorlagen, die fuer diesen Export nicht (mehr) erzeugbar sind.
          if (
            Object.keys(body?.parameters?.violations ?? {}).some((key) =>
              key.startsWith('exportTemplates'),
            )
          ) {
            this.messageService.error('consultant.export.templatesUnavailable');
            return;
          }
          if (body?.title === 'Python exporter error') {
            this.messageService.error('consultant.export.exporterDown');
            return;
          }
          const detail = this.backendErrorDetail(body);
          if (detail) {
            this.messageService.errorWithDetail(
              'consultant.export.error',
              detail,
            );
          } else {
            this.messageService.error('consultant.export.error');
          }
        },
      });
  }

  /** Bei responseType 'blob' kommt auch der Problem-Body eines Fehlers als Blob. */
  private async readErrorBody(err: {
    error?: unknown;
  }): Promise<ExportErrorBody | undefined> {
    try {
      return err?.error instanceof Blob
        ? JSON.parse(await err.error.text())
        : (err?.error as ExportErrorBody | undefined);
    } catch {
      return undefined;
    }
  }

  private backendErrorDetail(
    body: ExportErrorBody | undefined,
  ): string | undefined {
    const violations = body?.parameters?.violations;
    if (violations && Object.keys(violations).length > 0) {
      return Object.values(violations).join(', ');
    }
    return body?.message ?? undefined;
  }

  private parseContentDispositionFilename(
    disposition: string | null,
  ): string | undefined {
    if (!disposition) return undefined;
    const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
    if (utf8Match) return decodeURIComponent(utf8Match[1].trim());
    const match = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
    return match ? decodeURIComponent(match[1].trim()) : undefined;
  }

  /**
   * NP20-958/NP20-1064: Beide Export-Typen tragen einen sprechenden Namen und unterscheiden
   * sich nur im Typ-Segment. Der Name aus Content-Disposition hat Vorrang; dieser Fallback
   * greift nur, wenn der Header fehlt — und bildet dann bewusst dieselbe Regel ab wie
   * ExportFileNameBuilder im Backend, damit ein Download nicht je nach Weg anders heisst.
   */
  private fallbackExportFilename(
    mime: string | undefined,
    language: ExportLanguage,
  ): string {
    const ext = fileExtensionFor(mime);
    // Leere Namensteile fallen weg, statt als leeres Segment zu erscheinen.
    const parts = [
      this.twoPagerMode ? 'TwoPager' : 'Profil',
      this.sanitizeFilenamePart(this.selectedBerater?.nachname),
      this.sanitizeFilenamePart(this.selectedBerater?.vorname),
      language,
      this.todayInLocalTime(),
    ].filter((part) => part.length > 0);
    return `${parts.join('_')}.${ext}`;
  }

  /**
   * Heute in der Zeitzone des Browsers. `toISOString()` waere UTC und benennt die Datei
   * zwischen Mitternacht und 02:00 MESZ einen Tag zu frueh — das Backend bildet das Datum
   * aus seiner eigenen Zone, der Fallback soll denselben Namen treffen.
   */
  private todayInLocalTime(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  /** Wie im Backend: Umlaute transliteriert, alles ausserhalb [A-Za-z0-9] entfaellt. */
  private sanitizeFilenamePart(value: string | undefined): string {
    return (value ?? '')
      .trim()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/Ä/g, 'Ae')
      .replace(/Ö/g, 'Oe')
      .replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .replace(/[^A-Za-z0-9]/g, '');
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private beginPreselection(): void {
    this.showPreselectionHint = true;
    this.preselectionBaseline.clear();
    this.preselectionReports.clear();
    this.preselectionRun++;
  }

  private endPreselection(): void {
    this.showPreselectionHint = false;
  }

  /**
   * Der Hinweis gilt, solange die Vorauswahl vollstaendig ist. Kachel-Meldungen beim Oeffnen
   * tragen nur nach — verschachtelte Kacheln und spaet eintreffende Daten melden ihre Ids
   * erst mit Verzoegerung, ohne dass jemand etwas entschieden haette. Weggenommen wird der
   * Hinweis deshalb erst, wenn eine Meldung eine Id fallen laesst, die der Abschnitt vorher
   * schon getragen hat — bei vollstaendiger Vorauswahl kann das nur eine Abwahl sein.
   * Verglichen werden Mengen: dieselbe Auswahl darf in anderer Reihenfolge erneut kommen.
   */
  private notePreselectionReport(section: string, ids: string[]): void {
    if (!this.showPreselectionHint || this.applyingConfig) return;
    this.preselectionReports.set(section, new Set(ids));
    this.schedulePreselectionSettle();
  }

  /**
   * Gerechnet wird erst am Ende des Durchlaufs, und nur gegen die dann letzte Meldung je
   * Abschnitt: eine Kachel, die sich beim Aufbau selbst leert und im selben Durchlauf wieder
   * fuellt, hat nichts abgewaehlt. Zwei Klicks der Nutzerin liegen dagegen nie im selben
   * Durchlauf. Ausserdem ist die Leiste unten dann fertig geprueft (NG0100).
   */
  private schedulePreselectionSettle(): void {
    if (this.preselectionSettlePending) return;
    this.preselectionSettlePending = true;
    const run = this.preselectionRun;
    setTimeout(() => {
      this.preselectionSettlePending = false;
      if (run === this.preselectionRun) this.settlePreselection();
    }, 0);
  }

  private settlePreselection(): void {
    if (!this.showPreselectionHint) return;
    const reports = [...this.preselectionReports];
    this.preselectionReports.clear();

    for (const [section, reported] of reports) {
      const known = this.preselectionBaseline.get(section) ?? new Set<string>();
      if ([...known].some((id) => !reported.has(id))) {
        this.endPreselection();
        return;
      }
      reported.forEach((id) => known.add(id));
      this.preselectionBaseline.set(section, known);
    }
  }

  // Gemeinsame Skill-Auswahl (Fähigkeiten-Kachel + Task-Skills). Erzwingt das Budget zentral,
  // damit über beide Quellen zusammen nie mehr Zeilen (Skills + Kategorien) gewählt werden als
  // die Skill-Matrix hat.
  onSkillsSelectionChange(ids: string[]): void {
    if (
      this.twoPagerMode &&
      usedSkillRows(this.selectedBerater?.faehigkeiten, ids) > SKILL_MATRIX_ROWS
    ) {
      this.messageService.warning(
        'consultant.twoPager.skillLimitReached',
        undefined,
        { max: SKILL_MATRIX_ROWS },
      );
      return;
    }
    this.onExportSelectionChange(ids, 'skills');
  }

  onExportSelectionChange(ids: string[], section: string): void {
    this.storeExportSelection(ids, section);
    this.notePreselectionReport(section, ids);
  }

  /**
   * Die Projekt-Kachel meldet ihre Auswahl zusaetzlich flach und traegt dabei nur die Aufgaben
   * des zuletzt aufgeklappten Projekts — fuer den Hinweis zaehlt allein `projectsExportData`
   * mit seiner vollstaendigen Zuordnung.
   */
  onProjectListSelectionChange(ids: string[], section: string): void {
    this.storeExportSelection(ids, section);
  }

  private storeExportSelection(ids: string[], section: string): void {
    this.exportSelections[section] = ids;
    // viewReady suppresses emissions during initial data load — those are not user changes.
    // setTimeout prevents NG0100: child emissions during a CD cycle must not mutate parent state synchronously.
    if (!this.applyingConfig && this.viewReady) {
      setTimeout(() => (this.configDirty = true), 0);
    }
  }

  onProjectsExportDataChange(data: ProjectsExportData): void {
    this.projectsExportData = data;
    this.notePreselectionReport('projects', projectsExportIds(data));
    // viewReady suppresses emissions during initial data load — those are not user changes.
    // setTimeout prevents NG0100: child emissions during a CD cycle must not mutate parent state synchronously.
    if (!this.applyingConfig && this.viewReady) {
      setTimeout(() => (this.configDirty = true), 0);
    }
  }

  buildExportPayload(name: string): ExportPayload {
    const berater = this.selectedBerater;
    const sel = this.exportSelections;
    const projData = this.projectsExportData;

    const globalSkillIds = sel['skills'] ?? [];

    const projektSelections = projData.selectedProjectUuids.map(
      (projectUuid) => {
        const project = berater.projekte?.find((p) => p.uuid === projectUuid);
        const selectedTaskUuids = projData.taskSelections[projectUuid] ?? [];
        const selectedMethodItems =
          projData.methodSelections[projectUuid] ?? [];

        const aufgabenSelections = selectedTaskUuids.map((taskUuid) => {
          // Skills stammen aus der gemeinsamen Auswahl (Kachel + Task-Skills, synchron gehalten):
          // je Task die Schnittmenge aus Task-Skills und global ausgewählten Skills — in beiden Export-Modi.
          const task = project?.tasks?.find((t) => t.uuid === taskUuid);
          const faehigkeitIds = (task?.skills ?? [])
            .filter((s) => globalSkillIds.includes(s.id))
            .map((s) => s.id);

          return { aufgabeUuid: taskUuid, faehigkeitIds };
        });

        const selectedBranchNameIds = (project?.branchNames ?? []).map(
          (b) => b.id,
        );

        return {
          projektUuid: projectUuid,
          aufgabenSelections,
          selectedBranchNameIds,
          selectedMethodikItems: selectedMethodItems,
          selectedFieldPaths: [],
        };
      },
    );

    const pd = berater.personaldaten;
    let selectedFieldPaths: string[];
    if (!this.twoPagerMode) {
      selectedFieldPaths = [
        'vorname',
        'nachname',
        'personaldaten.intro',
        'personaldaten.jahrgang',
        'personaldaten.nationalitaet',
        'personaldaten.wohnort',
        'personaldaten.position',
        'personaldaten.beraterSeit',
        'personaldaten.verfuegbar',
        'personaldaten.reisebereitschaft',
        'personaldaten.einschraenkung',
        'personaldaten.photos',
        'personaldaten.sprachen',
        'personaldaten.branchen',
        'personaldaten.securityCheck',
        'ausbildung',
        'werdegang',
        'schulungen',
        'zertifikate',
      ];
      if (pd?.schwerpunkte?.length)
        selectedFieldPaths.push('personaldaten.schwerpunkte');
      if (projData.selectedProjectUuids.length) {
        selectedFieldPaths.push(
          'projekte.beschreibung',
          'projekte.tasks',
          'projekte.branchNames',
          'projekte.methodik',
        );
      }
    } else {
      selectedFieldPaths = [
        'vorname',
        'nachname',
        'personaldaten.intro',
        'personaldaten.photos',
      ];
      if (sel['personal']?.length)
        selectedFieldPaths.push('personaldaten.schwerpunkte');
      if (sel['industries']?.length)
        selectedFieldPaths.push('personaldaten.branchen');
      if (projData.selectedProjectUuids.length) {
        selectedFieldPaths.push(
          'projekte.beschreibung',
          'projekte.tasks',
          'projekte.branchNames',
          'projekte.methodik',
        );
      }
      if (sel['education']?.length) selectedFieldPaths.push('ausbildung');
      selectedFieldPaths.push('werdegang');
      if (sel['trainings']?.length) selectedFieldPaths.push('schulungen');
      if (sel['certificates']?.length) selectedFieldPaths.push('zertifikate');
    }

    const selectedKatalogIds: string[] = sel['rolesBa'] ?? [];
    const katalogSelections = selectedKatalogIds.map((katalogId) => {
      const katalog = this.assignedCatalogs.find((k) => k.id === katalogId);
      const selectedKenntnisUuids = (katalog?.kenntnisse ?? [])
        .map((k) => k.uuid)
        .filter((u): u is string => !!u);
      return { katalogId, selectedKenntnisUuids };
    });

    // NP20-958: Bewertung je gewaehlter Faehigkeit. Quelle ist das Profil-Level — im Export
    // wird nicht neu bewertet, die Vorlage haelt damit den Stand zum Speicherzeitpunkt fest.
    const skillRatings: Record<string, SkillRating> = {};
    for (const id of globalSkillIds) {
      const rating = toSkillRating(
        berater.faehigkeiten?.find((f) => f.id === id)?.level,
      );
      if (rating) skillRatings[id] = rating;
    }

    const payload: ExportPayload = {
      name,
      type: this.twoPagerMode ? 'TWO_PAGER' : 'STANDARD',
      language: this.exportLanguage,
      includeProfilePicture: this.includeProfilePicture,
      // Das Backend erwartet pageCount nur bei TWO_PAGER; im STANDARD-Export bleibt es bewusst undefined.
      pageCount: this.twoPagerMode ? this.twoPagerPageCount : undefined,
      selectedFieldPaths,
      projektSelections,
      selectedSchulungUuids: sel['trainings'] ?? [],
      selectedZertifikatUuids: sel['certificates'] ?? [],
      selectedAusbildungUuids: sel['education'] ?? [],
      selectedWerdegangUuids: this.twoPagerMode
        ? (berater.werdegang ?? [])
            .map((w) => w.uuid)
            .filter((u): u is string => !!u)
        : (sel['career'] ?? []),
      katalogSelections,
      selectedSchwerpunkte: !this.twoPagerMode
        ? (pd?.schwerpunkte ?? [])
        : (sel['personal'] ?? []),
      selectedBranchen: !this.twoPagerMode
        ? (pd?.branchen ?? [])
        : (sel['industries'] ?? []),
      selectedSprachen: !this.twoPagerMode ? normalizeToLanguageEntries(pd?.sprachen).map((e) => e.language) : [],
      selectedSecurityCheckTypes: !this.twoPagerMode
        ? (pd?.securityCheck ?? []).map((s) => s.typ)
        : [],
      selectedFaehigkeitIds: globalSkillIds,
      skillRatings,
      exportTemplates: this.twoPagerMode
        ? undefined
        : normalizeExportTemplates(this.exportTemplates, this.availableTemplates),
    };

    return payload;
  }
}
